import React, { useState, useRef, useEffect, useCallback } from 'react';
import ZipCodes from '../_libs/zip-codes';
import ZipcodePopover from '../_cart/zipcode-popover';
import Button from '../_common/button';
import translate from '../_utils/tools/translate';
import { builderDispatch } from '../_utils/ui';
import {
  isPostalCodeLocal,
  shouldStripPostalCodeWhitespace,
} from '../_utils/validations';
import CartItemsChangeEvents from '../_cart/cart-items-change-events';

function StockAvailability({ currentProducts, selectedDeliveryOption }) {
  if (
    currentProducts.length > 1 ||
    !currentProducts[0]?.check_stock_availability
  ) {
    return null;
  }

  if (selectedDeliveryOption === "2") {
    return null;
  }

  const [zipCode, setZipCode] = useState(() => ZipCodes.load());
  const existingZipCode = ZipCodes.load();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [zipCodeValid, setZipCodeValid] = useState(() =>
    isPostalCodeLocal(existingZipCode),
  );

  const postalCodeInputRef = useRef(null);

  const togglePopover = (e) => {
    e.preventDefault();
    setPopoverOpen((prev) => !prev);
  };

  const handleZipCodeChange = useCallback((event) => {
    setZipCode(event.target.value);
    zipCodeValid(isPostalCodeLocal(zipCode));
  }, []);

  const handleZipCodeKeyUp = useCallback((event) => {
    if (event.key === 'Enter' && zipCodeValid) {
      handleZipCodeUpdateClick();
    }
  }, []);

  const handleZipCodeUpdateClick = () => {
    if (shouldStripPostalCodeWhitespace()) {
      setZipCode(zipCode.replace(/ /g, ''));
    }

    if (zipCodeValid) {
      builderDispatch.dispatch({
        type: CartItemsChangeEvents.ZIP_CODE_CHANGE,
        value: zipCode,
      });
      setPopoverOpen(false);
    }
  };

  const handleZipCodeChangeContext = useCallback((newZipCode) => {
    if (!isPostalCodeLocal(newZipCode)) return;
    ZipCodes.save(newZipCode);
  }, []);

  const handleZipCodeCancelClick = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  useEffect(() => {
    const initialZipCodeValid = isPostalCodeLocal(zipCode);
    setZipCodeValid(initialZipCodeValid);

    if (!initialZipCodeValid) {
      setPopoverOpen(true);
    }

    const builderDispatchToken = builderDispatch.register((payload) => {
      if (payload.type === CartItemsChangeEvents.ZIP_CODE_CHANGE) {
        handleZipCodeChangeContext(payload.value);
      }
    });

    return () => {
      builderDispatch.unregister(builderDispatchToken);
    };
  }, [zipCode, handleZipCodeChangeContext]);

  useEffect(() => {
    if (popoverOpen) {
      const timer = setTimeout(() => {
        if (postalCodeInputRef.current) {
          postalCodeInputRef.current.focus();
        }
      }, 100)
    }
  }, [popoverOpen]);

  const popperOptions = {
    modifiers: [
      {
        name: 'flip',
        enabled: false,
      },
    ],
  };

  return (
    <div>
      <ZipcodePopover
        onRef={(el) => (postalCodeInputRef.current = el)}
        popoverOpen={popoverOpen}
        zipCodeValid={zipCodeValid}
        zipCode={existingZipCode}
        handleZipCodeChange={handleZipCodeChange}
        handleZipCodeKeyUp={handleZipCodeKeyUp}
        handleZipCodeUpdateClick={handleZipCodeUpdateClick}
        handleZipCodeCancelClick={handleZipCodeCancelClick}
        popperOptions={popperOptions}
        shouldInputAutofocus={false}
      />
      <p>
        Delivery to
        <span className="delivery-zip-code mr-2">{existingZipCode}</span>
        <Button
          className="btn-outline-info rounded-pill mb-1 mb-md-1 mr-md-2"
          id="popover-anchor"
          aria-label={`Change ${translate('zip code')}`}
          text="Change"
          onClick={togglePopover}
        />
      </p>
    </div>
  );
}

export default StockAvailability;
